/**
 * Helper functions for the Benchmark charts
 */

// Colors used for chart lines - high contrast, visually distinct palette
export const CHART_COLORS = [
  '#3366CC', // strong blue
  '#DC3912', // red
  '#FF9900', // orange
  '#109618', // green
  '#990099', // purple
  '#0099C6', // teal blue
  '#DD4477', // pink
  '#66AA00', // lime green
  '#B82E2E', // dark red
  '#316395', // slate blue
  '#994499', // dark purple
  '#22AA99', // sea green
  '#AAAA11', // olive
  '#6633CC', // violet
  '#E67300', // orange-brown
  '#329262', // forest green
  '#5574A6', // blue-grey
  '#3B3EAC', // indigo
  '#B77322', // brown
  '#16D620', // bright green
];

// Jua brand pink - used for all Jua models
const JUA_PINK = '#E34290';

// Model-specific color assignments to ensure consistency and visual distinction
export const MODEL_COLORS: Record<string, string> = {
  'ECMWF IFS': CHART_COLORS[0], // strong blue
  'ECMWF IFS HRES': CHART_COLORS[9], // slate blue (different from regular ECMWF)
  GFS: CHART_COLORS[1], // red
  'EPT-1.5': JUA_PINK, // Jua brand pink
  'EPT-1.5 Early': JUA_PINK, // Jua brand pink
  'EPT 2.0': JUA_PINK, // Jua brand pink
  'EPT-2': JUA_PINK, // Jua brand pink
  'EPT-2 v1': JUA_PINK, // Jua brand pink (legacy, should not appear in new data)
  'EPT-2 v2': JUA_PINK, // Jua brand pink (legacy, should not appear in new data)
  AIFS1: CHART_COLORS[4], // purple
  AIFS2: CHART_COLORS[5], // teal blue
  ICON: CHART_COLORS[6], // pink
  'ICON-EU': CHART_COLORS[7], // lime green
  'MeteoFrance Arpege': CHART_COLORS[10], // dark purple
  'UK Met Office': CHART_COLORS[11], // sea green
  ERA5: CHART_COLORS[12], // olive
  'ECMWF ENS Mean': CHART_COLORS[13], // violet (different from ECMWF IFS)
  Aurora: CHART_COLORS[14], // orange-brown
  GEM: CHART_COLORS[15], // forest green
  JMA: CHART_COLORS[16], // blue-grey
  KMA: CHART_COLORS[17], // indigo
};

// Keep track of colors assigned to models in the current chart
const usedColors = new Map<string, string>();
// Track which models are Jua models (to apply different line styles)
// const JUA_MODELS = ['EPT-1.5', 'EPT-1.5 Early', 'EPT 2.0', 'EPT-2 v1', 'EPT-2 v2'];

/**
 * Get a consistent color for a model
 * @param model - The model name
 * @param index - The fallback index for models without predefined colors
 */
function getModelColor(model: string, index: number): string {
  // Reset the map of used colors at the start of each chart creation
  if (index === 0) {
    usedColors.clear();
  }

  // If we already assigned a color to this model in this chart, use that
  if (usedColors.has(model)) {
    return usedColors.get(model)!;
  }

  // If the model has a predefined color, use that
  if (MODEL_COLORS[model]) {
    const color = MODEL_COLORS[model];
    usedColors.set(model, color);
    return color;
  }

  // Special case: Any model starting with "EPT" is a Jua model and should get the pink color
  if (model.startsWith('EPT')) {
    usedColors.set(model, JUA_PINK);
    return JUA_PINK;
  }

  // Find an unused color from the palette
  const usedColorValues = Array.from(usedColors.values());
  const availableColor = CHART_COLORS.find((color) => !usedColorValues.includes(color));

  if (availableColor) {
    // We found an unused color, assign it to this model
    usedColors.set(model, availableColor);
    return availableColor;
  }

  // If all colors are used, fall back to using the index to cycle through colors
  const fallbackColor = CHART_COLORS[index % CHART_COLORS.length];
  usedColors.set(model, fallbackColor);
  return fallbackColor;
}

/**
 * Creates a data zoom configuration for the ECharts benchmark chart
 */
export function getDataZoomConfig(isDark: boolean): any[] {
  return [
    {
      type: 'slider',
      show: true,
      xAxisIndex: [0],
      bottom: '2%',
      height: 20,
      borderColor: isDark ? '#374151' : '#E5E7EB',
      textStyle: {
        color: isDark ? '#E5E7EB' : '#666666',
      },
      handleStyle: {
        color: isDark ? '#4B5563' : '#9CA3AF',
        borderColor: isDark ? '#6B7280' : '#D1D5DB',
      },
      moveHandleStyle: {
        color: isDark ? '#4B5563' : '#9CA3AF',
      },
      selectedDataBackground: {
        lineStyle: {
          color: isDark ? '#6B7280' : '#D1D5DB',
        },
        areaStyle: {
          color: isDark ? '#4B5563' : '#F3F4F6',
        },
      },
      start: 0,
      end: 100,
      filterMode: 'none', // Don't filter empty values
    },
    {
      type: 'inside',
      xAxisIndex: [0],
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
      filterMode: 'none', // Don't filter empty values
      start: 0,
      end: 100,
    },
  ];
}

/**
 * Filter data by the selected variable to remove any invalid rows
 */
export function filterDataByVariable(data: any[], selectedVariable: string): any[] {
  if (!data || data.length === 0 || !selectedVariable) {
    return [];
  }

  // Only include rows where the selected variable has valid values
  return data.filter(
    (row) =>
      row[selectedVariable] !== null &&
      row[selectedVariable] !== undefined &&
      !isNaN(row[selectedVariable]),
  );
}

/**
 * Filter out points with null or invalid values
 */
// function filterValidPoints(data: any[], variable: string) {
//   return data.filter(item => {
//     const value = item[variable];
//     return value !== null && value !== undefined && !isNaN(value);
//   });
// }

/**
 * Creates the options configuration for the ECharts benchmark chart
 */
export function createBenchmarkChartOptions(
  data: any[],
  selectedVariable: string,
  mode: 'absolute' | 'skill',
  isDark: boolean,
  referenceModel?: string,
) {
  const axisColor = isDark ? '#E5E7EB' : '#666666';

  // Safeguard against empty data or missing variable
  if (!data || data.length === 0 || !selectedVariable) {
    return {
      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
      series: [],
    };
  }

  // First ensure we're only working with data that has valid values for the selected variable
  const validData = filterDataByVariable(data, selectedVariable);

  // If no valid data after filtering, return empty chart config
  if (validData.length === 0) {
    return {
      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
      series: [],
    };
  }

  // Filter out distinct model names from the valid data
  const validModels = Array.from(new Set(validData.map((d) => d.Model)));

  // If skill mode and reference model is not among valid models, return empty chart
  if (mode === 'skill' && referenceModel && !validModels.includes(referenceModel)) {
    return {
      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
      series: [],
    };
  }

  // Create series for each valid model
  const series = validModels
    .map((model, index) => {
      // Filter the data to only include rows for this model
      const modelData = validData.filter((d) => d.Model === model);

      // Skip models with no data points
      if (modelData.length === 0) {
        return null;
      }

      // Determine if this is the reference model in skill mode
      const isReferenceModel = model === referenceModel && mode === 'skill';

      // Get consistent color for this model
      const modelColor = getModelColor(model, index);

      // Determine line style based on model type
      let lineType = 'solid';

      // Different line styles for Jua models to differentiate them
      if (model === 'EPT-1.5') {
        lineType = 'solid';
      } else if (model === 'EPT-1.5 Early') {
        lineType = 'dashed';
      } else if (model === 'EPT 2.0') {
        lineType = 'dotted';
      } else if (model === 'EPT-2 v1') {
        lineType = 'dashed';
      } else if (model === 'EPT-2 v2') {
        lineType = 'solid';
      }

      // Use dashed line for reference model regardless
      if (isReferenceModel) {
        lineType = 'dashed';
      }

      return {
        name: model + (isReferenceModel ? ' (Reference)' : ''),
        type: 'line',
        smooth: false, // Keep false to better represent actual data points
        showSymbol: false, // Don't show symbols/dots on the lines
        data: modelData.map((item) => [item['Prediction timedelta'], item[selectedVariable]]),
        connectNulls: false, // Don't connect across null values
        lineStyle: {
          width: isReferenceModel ? 3 : 2,
          type: lineType,
          color: modelColor,
        },
        itemStyle: {
          color: modelColor,
        },
      };
    })
    .filter(Boolean); // Filter out any null series

  return {
    backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
    tooltip: {
      trigger: 'axis',
      formatter: function (params: any) {
        let tooltipText = `Time Delta: ${params[0].value[0]} hours<br/>`;
        params.forEach((param: any) => {
          // Skip null values in tooltip
          if (param.value[1] === null || param.value[1] === undefined || isNaN(param.value[1])) {
            return;
          }

          tooltipText += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
          tooltipText += `${param.seriesName}: ${param.value[1]?.toFixed?.(3)}${mode === 'skill' ? '%' : ''}<br/>`;
        });
        return tooltipText;
      },
      backgroundColor: '#fff',
      textStyle: { color: '#000' },
      borderWidth: 1,
      borderColor: isDark ? '#4B5563' : '#E5E7EB',
    },
    legend: {
      data: validModels.map((model) =>
        model === referenceModel && mode === 'skill' ? `${model} (Reference)` : model,
      ),
      textStyle: { color: axisColor },
      top: 10,
      type: 'scroll',
      itemWidth: 14,
      itemHeight: 14,
      itemGap: 12,
      formatter: function (name: string) {
        // Format the label to ensure the reference model is marked
        return name;
      },
      icon: 'rect',
    },
    grid: {
      left: '10%',
      right: '5%',
      bottom: '15%',
      top: '15%',
      containLabel: true,
    },
    dataZoom: getDataZoomConfig(isDark),
    xAxis: {
      type: 'value',
      name: 'Prediction Time Delta (hours)',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: {
        color: axisColor,
      },
      axisLine: {
        lineStyle: {
          color: axisColor,
        },
      },
      axisLabel: {
        color: axisColor,
        fontSize: 12,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: isDark ? '#374151' : '#f0f0f0',
          type: 'dashed',
        },
      },
    },
    yAxis: {
      type: 'value',
      name: mode === 'skill' ? 'Skill Score [%]' : selectedVariable,
      nameLocation: 'middle',
      nameGap: 50,
      nameTextStyle: {
        color: axisColor,
      },
      axisLine: {
        lineStyle: {
          color: axisColor,
        },
      },
      axisLabel: {
        color: axisColor,
        fontSize: 12,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: isDark ? '#374151' : '#f0f0f0',
          type: 'dashed',
        },
      },
    },
    series: series,
  };
}
